
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlusCircle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { EcommercePlatform } from '@/components/admin/campaigns/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface EcommerceCampaign {
  id: string;
  name: string;
  platform: EcommercePlatform;
  status: 'active' | 'paused' | 'draft';
  rewardType: string;
  rewardValue: string;
  impressions: number;
  conversions: number;
  createdAt: Date;
}

const EcommerceCampaignsList: React.FC = () => {
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  
  // Mock campaigns for demonstration
  const mockCampaigns: EcommerceCampaign[] = [
    {
      id: '1',
      name: 'Summer Sale Referrals',
      platform: 'shopify',
      status: 'active',
      rewardType: 'fixed',
      rewardValue: '$10 off',
      impressions: 2450,
      conversions: 128,
      createdAt: new Date(2023, 5, 15),
    },
    {
      id: '2',
      name: 'New Customer Discount',
      platform: 'woocommerce',
      status: 'active',
      rewardType: 'percentage',
      rewardValue: '15% off',
      impressions: 1830,
      conversions: 95,
      createdAt: new Date(2023, 4, 10),
    },
    {
      id: '3',
      name: 'Holiday Season Promo',
      platform: 'shopify',
      status: 'draft',
      rewardType: 'fixed',
      rewardValue: '$25 off',
      impressions: 0,
      conversions: 0,
      createdAt: new Date(2023, 10, 1),
    },
    {
      id: '4',
      name: 'Free Shipping Campaign',
      platform: 'bigcommerce',
      status: 'paused',
      rewardType: 'free_shipping',
      rewardValue: 'Free Shipping',
      impressions: 945,
      conversions: 43,
      createdAt: new Date(2023, 3, 20),
    },
  ];
  
  const filteredCampaigns = platformFilter === 'all' 
    ? mockCampaigns 
    : mockCampaigns.filter(campaign => campaign.platform === platformFilter);
  
  const getPlatformName = (platform: EcommercePlatform): string => {
    const platformMap: Record<EcommercePlatform, string> = {
      shopify: 'Shopify',
      woocommerce: 'WooCommerce',
      bigcommerce: 'BigCommerce',
      volusion: 'Volusion',
      wordpress: 'WordPress',
      squarespace: 'Squarespace',
      magento: 'Magento',
      shift4shop: 'Shift4Shop'
    };
    
    return platformMap[platform] || platform;
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'paused':
        return <Badge variant="outline" className="text-amber-500 border-amber-500">Paused</Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle>E-commerce Campaigns</CardTitle>
          <CardDescription>
            Manage your e-commerce referral campaigns
          </CardDescription>
        </div>
        <Button size="sm">
          <PlusCircle className="mr-2 h-4 w-4" />
          New Campaign
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium">Filter by platform:</span>
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Platforms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                <SelectItem value="shopify">Shopify</SelectItem>
                <SelectItem value="woocommerce">WooCommerce</SelectItem>
                <SelectItem value="bigcommerce">BigCommerce</SelectItem>
                <SelectItem value="volusion">Volusion</SelectItem>
                <SelectItem value="wordpress">WordPress</SelectItem>
                <SelectItem value="squarespace">Squarespace</SelectItem>
                <SelectItem value="magento">Magento</SelectItem>
                <SelectItem value="shift4shop">Shift4Shop</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Campaign Name</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reward</TableHead>
              <TableHead className="text-right">Impressions</TableHead>
              <TableHead className="text-right">Conversions</TableHead>
              <TableHead className="text-right">Conversion Rate</TableHead>
              <TableHead className="text-right">Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCampaigns.map((campaign) => (
              <TableRow key={campaign.id}>
                <TableCell className="font-medium">{campaign.name}</TableCell>
                <TableCell>{getPlatformName(campaign.platform)}</TableCell>
                <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                <TableCell>{campaign.rewardValue}</TableCell>
                <TableCell className="text-right">{campaign.impressions.toLocaleString()}</TableCell>
                <TableCell className="text-right">{campaign.conversions.toLocaleString()}</TableCell>
                <TableCell className="text-right">
                  {campaign.impressions > 0 
                    ? `${((campaign.conversions / campaign.impressions) * 100).toFixed(2)}%` 
                    : '-'}
                </TableCell>
                <TableCell className="text-right">{campaign.createdAt.toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm">Edit</Button>
                </TableCell>
              </TableRow>
            ))}
            
            {filteredCampaigns.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-4 text-muted-foreground">
                  No campaigns found for the selected platform.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default EcommerceCampaignsList;
